import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { CheckCircle, Home } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuickSignInPage = () => {
  const { agentId } = useParams();
  const [agentInfo, setAgentInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    description: ''
  });

  useEffect(() => {
    fetchAgentInfo();
  }, [agentId]);

  const fetchAgentInfo = async () => {
    try {
      const response = await axios.get(`${API}/public/quick-signin/${agentId}`);
      setAgentInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch agent info:', error);
      toast.error('Invalid or expired link');
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData({ ...formData, phone: formatted });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.full_name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Please enter a valid email');
      return;
    }
    if (!formData.phone.trim()) {
      toast.error('Please enter your phone number');
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(`${API}/quick-signin/submit?agent_id=${agentId}`, {
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone,
        description: formData.description || null
      });

      setSubmitted(true);
      toast.success('Thank you! Your information has been submitted.');
    } catch (error) {
      console.error('Failed to submit:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A4D2E]"></div>
      </div>
    );
  }

  if (!agentInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Home className="text-red-600" size={32} />
          </div>
          <h2 className="text-2xl font-bold text-stone-900 mb-2">Invalid Link</h2>
          <p className="text-stone-600">
            This sign-in link is invalid or has expired. Please contact the agent for a valid link.
          </p>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="text-green-600" size={32} />
          </div>
          <h2 className="text-2xl font-bold text-stone-900 mb-2">Thank You!</h2>
          <p className="text-stone-600 mb-4">
            Your information has been submitted successfully. {agentInfo.name} will be in touch with you soon.
          </p>
          <p className="text-sm text-stone-500">
            You can safely close this page.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100 flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-6 md:p-8">
        <div className="text-center mb-6">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1A4D2E] mb-2" style={{fontFamily: '"Playfair Display", serif'}}>
            Quick Sign-In
          </h1>
          <p className="text-stone-600">
            Please share your contact information with <strong>{agentInfo.name}</strong>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Full Name */}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Full Name <span className="text-red-500">*</span>
            </label>
            <Input
              type="text"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              placeholder="Enter your full name"
              required
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="your.email@example.com"
              required
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <Input
              type="tel"
              value={formData.phone}
              onChange={handlePhoneChange}
              placeholder="(123) 456-7890"
              maxLength={14}
              required
            />
          </div>

          {/* Description (Optional) */}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Additional Notes (Optional)
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Any additional information you'd like to share..."
              rows={3}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-[#1A4D2E] focus:border-transparent resize-none"
            />
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#1A4D2E] hover:bg-[#15401F] text-white py-3"
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </Button>
        </form>

        <p className="text-xs text-stone-500 text-center mt-6">
          By submitting this form, you agree to be contacted by {agentInfo.name} regarding real estate opportunities.
        </p>
      </Card>
    </div>
  );
};

export default QuickSignInPage;
